import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldOff, MessageSquare, CheckCircle2, XCircle, Clock, Send } from "lucide-react";

export default function CreatorAppeals() {
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(null); // movie object
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const [mine, mineAppeals] = await Promise.all([
      api.get("/movies/mine").then((r) => r.data).catch(() => []),
      api.get("/me/appeals").then((r) => r.data).catch(() => []),
    ]);
    setMovies(mine);
    setAppeals(mineAppeals);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!drafting) return;
    if (message.trim().length < 10) {
      toast.error("Please write at least 10 characters explaining your appeal.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/me/movies/${drafting.id}/appeal`, { message: message.trim() });
      toast.success("Appeal submitted. We'll be in touch by email.");
      setDrafting(null);
      setMessage("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit appeal");
    } finally {
      setSubmitting(false);
    }
  };

  const removedMovies = movies.filter((m) => m.status === "removed");
  const appealsByMovie = new Map(appeals.map((a) => [a.movie_id, a]));

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12" data-testid="creator-appeals-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldOff className="h-5 w-5 text-yellow-400" />
        <p className="text-eyebrow text-yellow-400">Takedown appeals</p>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black mb-2 tracking-tighter">
        Disagree with a <span className="text-[#E65F2B]">takedown</span>?
      </h1>
      <p className="text-white/60 max-w-2xl">
        Sometimes our moderators get it wrong. If one of your movies was removed and you believe it shouldn't have been, write a short appeal here. An admin will review and respond by email — usually within 48 hours.
      </p>

      {loading ? (
        <div className="mt-12 flex justify-center"><div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" /></div>
      ) : (
        <>
          {/* Existing appeals */}
          {appeals.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold mb-4">Your appeals</h2>
              <div className="space-y-4">
                {appeals.map((a) => (
                  <div key={a.id} className="card-surface p-5" data-testid={`appeal-${a.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-display font-bold">{a.movie_title}</p>
                          {a.status === "pending" && (
                            <span className="badge-pill text-[10px] bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" /> under review
                            </span>
                          )}
                          {a.status === "approved" && (
                            <span className="badge-pill text-[10px] bg-green-500/15 text-green-400 border border-green-500/30 inline-flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> restored
                            </span>
                          )}
                          {a.status === "dismissed" && (
                            <span className="badge-pill text-[10px] bg-red-500/15 text-red-400 border border-red-500/30 inline-flex items-center gap-1">
                              <XCircle className="h-3 w-3" /> dismissed
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-white/40 mt-2">
                          Submitted {new Date(a.created_at).toLocaleString()}
                          {a.decided_at && <> · Decided {new Date(a.decided_at).toLocaleString()}</>}
                        </p>
                        {a.remove_reason && (
                          <p className="text-xs text-white/50 mt-2">
                            <span className="text-white/40">Original takedown reason:</span> {a.remove_reason}
                          </p>
                        )}
                        <div className="mt-3 bg-white/5 rounded-lg p-3 border-l-2 border-[#E65F2B]/40">
                          <p className="text-xs text-[#E65F2B] mb-1">Your appeal</p>
                          <p className="text-sm whitespace-pre-wrap">{a.message}</p>
                        </div>
                        {a.admin_note && (
                          <div className={`mt-3 rounded-lg p-3 border-l-2 ${a.status === "approved" ? "bg-green-500/5 border-green-500/40" : "bg-red-500/5 border-red-500/40"}`}>
                            <p className={`text-xs mb-1 ${a.status === "approved" ? "text-green-400" : "text-red-400"}`}>Admin response</p>
                            <p className="text-sm whitespace-pre-wrap">{a.admin_note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Movies eligible for new appeal */}
          <section className="mt-10">
            <h2 className="font-display text-xl font-bold mb-4">Movies that can be appealed</h2>
            {removedMovies.length === 0 ? (
              <div className="card-surface p-10 text-center text-white/50" data-testid="appeals-empty">
                <ShieldOff className="h-8 w-8 mx-auto mb-3 text-white/20" />
                <p>No movies of yours have been removed. You're good to go.</p>
                <Link to="/creator" className="ember-btn mt-4 inline-flex">Back to dashboard</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {removedMovies.map((m) => {
                  const existing = appealsByMovie.get(m.id);
                  const blocked = existing && existing.status === "pending";
                  return (
                    <div key={m.id} className="card-surface p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`removed-movie-${m.id}`}>
                      <div className="min-w-0">
                        <p className="font-display font-bold">{m.title}</p>
                        <p className="text-xs text-white/40 mt-1">
                          {m.genre} · {m.channel_name}
                        </p>
                        {m.remove_reason && (
                          <p className="text-xs text-white/60 mt-2">
                            <span className="text-white/40">Moderator reason:</span> {m.remove_reason}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => { setDrafting(m); setMessage(""); }}
                        disabled={blocked}
                        className="ember-btn whitespace-nowrap disabled:opacity-40"
                        data-testid={`appeal-button-${m.id}`}
                      >
                        {blocked ? "Appeal pending" : "Appeal removal"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {/* Appeal draft modal */}
      {drafting && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="appeal-modal">
          <div className="card-surface w-full max-w-xl p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
                <MessageSquare className="h-5 w-5 text-[#E65F2B]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-2xl font-bold">Appeal "{drafting.title}"</h3>
                <p className="text-sm text-white/60 mt-2">
                  Tell us why you believe this movie should be restored. Be specific — context, sources, or counter-evidence helps moderators decide quickly.
                </p>
              </div>
            </div>
            <textarea
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={2000}
              placeholder="I believe my film should be reinstated because…"
              className="input-dark resize-none mt-5"
              disabled={submitting}
              data-testid="appeal-message-input"
            />
            <p className="text-xs text-white/30 mt-1">{message.length}/2000</p>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button onClick={() => setDrafting(null)} disabled={submitting} className="ghost-btn" data-testid="appeal-cancel">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting || message.trim().length < 10}
                className="ember-btn disabled:opacity-40"
                data-testid="appeal-submit"
              >
                <Send className="h-4 w-4" /> {submitting ? "Sending..." : "Submit appeal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
